import React, { useMemo, useState } from "react";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    LineChart,
    Line,
    Legend,
} from "recharts";
import {
    Building2,
    IndianRupee,
    Users,
    Cpu,
    TrendingUp,
    TrendingDown,
    ChevronDown,
    Download,
} from "lucide-react";

const CENTRE_DATA = [
    {
        centre: "Vizag",
        bookings: 312,
        revenue: 486000,
        utilization: 78,
        activeUsers: 145,
        machines: 12,
        growth: 12.4,
    },
    {
        centre: "Vijayawada",
        bookings: 268,
        revenue: 412000,
        utilization: 71,
        activeUsers: 128,
        machines: 10,
        growth: 8.1,
    },
    {
        centre: "Tirupati",
        bookings: 194,
        revenue: 305000,
        utilization: 64,
        activeUsers: 96,
        machines: 8,
        growth: -3.2,
    },
    {
        centre: "Kurnool",
        bookings: 156,
        revenue: 238000,
        utilization: 58,
        activeUsers: 74,
        machines: 7,
        growth: 5.6,
    },
    {
        centre: "Guntur",
        bookings: 121,
        revenue: 189000,
        utilization: 49,
        activeUsers: 61,
        machines: 6,
        growth: -1.8,
    },
    {
        centre: "Nellore",
        bookings: 98,
        revenue: 152000,
        utilization: 42,
        activeUsers: 47,
        machines: 5,
        growth: 3.4,
    },
];

const MONTHLY_TREND = [
    { month: "Feb", revenue: 980000, bookings: 720 },
    { month: "Mar", revenue: 1120000, bookings: 810 },
    { month: "Apr", revenue: 1050000, bookings: 765 },
    { month: "May", revenue: 1240000, bookings: 890 },
    { month: "Jun", revenue: 1380000, bookings: 960 },
    { month: "Jul", revenue: 1490000, bookings: 1020 },
];

const RANGE_OPTIONS = ["Last 6 months", "Last 3 months", "Last 30 days"];

function StatCard({ icon: Icon, label, value, tone, delta }) {
    const positive = delta >= 0;
    return (
        <div className="rounded-2xl border border-gray-200 bg-white p-5">
            <div className="flex items-center justify-between">
                <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${tone}`}>
                    <Icon className="h-5 w-5" />
                </span>
                {delta !== undefined && (
                    <span className={`flex items-center gap-1 text-xs font-semibold ${positive ? "text-green-600" : "text-red-500"}`}>
                        {positive ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
                        {Math.abs(delta)}%
                    </span>
                )}
            </div>
            <p className="mt-3 text-2xl font-bold text-gray-900">{value}</p>
            <p className="text-sm text-gray-500">{label}</p>
        </div>
    );
}

const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
        <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-md">
            <p className="text-xs font-semibold text-gray-700">{label}</p>
            {payload.map((entry) => (
                <p key={entry.dataKey} className="text-xs text-gray-500">
                    {entry.name}:{" "}
                    <span className="font-semibold text-gray-800">
                        {entry.dataKey === "revenue" ? `₹${entry.value.toLocaleString("en-IN")}` : entry.value}
                    </span>
                </p>
            ))}
        </div>
    );
};

export default function CentresAnalytics() {
    const [range, setRange] = useState("Last 6 months");

    const totals = useMemo(() => {
        const revenue = CENTRE_DATA.reduce((sum, c) => sum + c.revenue, 0);
        const bookings = CENTRE_DATA.reduce((sum, c) => sum + c.bookings, 0);
        const activeUsers = CENTRE_DATA.reduce((sum, c) => sum + c.activeUsers, 0);
        const avgUtilization = Math.round(
            CENTRE_DATA.reduce((sum, c) => sum + c.utilization, 0) / CENTRE_DATA.length
        );
        return { revenue, bookings, activeUsers, avgUtilization };
    }, []);

    return (
        <div className="h-screen w-full overflow-y-auto bg-gray-50 scrollbar-hide">
            <style>{`
                .scrollbar-hide::-webkit-scrollbar {
                    display: none;
                }
                .scrollbar-hide {
                    -ms-overflow-style: none;
                    scrollbar-width: none;
                }
            `}</style>

            <div className="p-6">
                {/* Page heading */}
                <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Centre analytics</h1>
                        <p className="mt-1 text-sm text-gray-500">
                            Performance report across all APIC centres in Andhra Pradesh
                        </p>
                    </div>

                    <div className="flex items-center gap-3">
                        <div className="relative">
                            <select
                                value={range}
                                onChange={(e) => setRange(e.target.value)}
                                className="appearance-none rounded-lg border border-gray-200 bg-white py-2 pl-3 pr-8 text-sm text-gray-700 outline-none focus:border-gray-300"
                            >
                                {RANGE_OPTIONS.map((r) => (
                                    <option key={r} value={r}>
                                        {r}
                                    </option>
                                ))}
                            </select>
                            <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                        </div>
                        <button className="flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700">
                            <Download className="h-4 w-4" />
                            Export
                        </button>
                    </div>
                </div>

                {/* Stat cards */}
                <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <StatCard
                        icon={IndianRupee}
                        label="Total revenue"
                        value={`₹${(totals.revenue / 100000).toFixed(1)}L`}
                        tone="bg-green-50 text-green-500"
                        delta={9.8}
                    />
                    <StatCard
                        icon={Building2}
                        label="Total bookings"
                        value={totals.bookings}
                        tone="bg-indigo-50 text-indigo-500"
                        delta={6.3}
                    />
                    <StatCard
                        icon={Users}
                        label="Active users"
                        value={totals.activeUsers}
                        tone="bg-blue-50 text-blue-500"
                        delta={4.1}
                    />
                    <StatCard
                        icon={Cpu}
                        label="Avg. utilization"
                        value={`${totals.avgUtilization}%`}
                        tone="bg-amber-50 text-amber-500"
                        delta={-1.5}
                    />
                </div>

                {/* Charts */}
                <div className="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
                    <div className="rounded-2xl border border-gray-200 bg-white p-6">
                        <h2 className="text-base font-bold text-gray-900">Revenue by centre</h2>
                        <p className="mt-0.5 text-sm text-gray-500">Total revenue generated per centre</p>
                        <div className="mt-4 h-72">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={CENTRE_DATA}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                    <XAxis dataKey="centre" tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} />
                                    <YAxis tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} tickFormatter={(v) => `₹${v / 1000}k`} />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Bar dataKey="revenue" name="Revenue" fill="#6366f1" radius={[6, 6, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    <div className="rounded-2xl border border-gray-200 bg-white p-6">
                        <h2 className="text-base font-bold text-gray-900">Monthly trend</h2>
                        <p className="mt-0.5 text-sm text-gray-500">Revenue and bookings across all centres</p>
                        <div className="mt-4 h-72">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={MONTHLY_TREND}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                    <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} />
                                    <YAxis tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} tickFormatter={(v) => `₹${v / 100000}L`} />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Legend wrapperStyle={{ fontSize: 12 }} />
                                    <Line type="monotone" dataKey="revenue" name="Revenue" stroke="#6366f1" strokeWidth={2.5} dot={{ r: 3 }} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>

                {/* Detailed table */}
                <div className="w-full rounded-2xl border border-gray-200 bg-white shadow-sm">
                    <div className="border-b border-gray-100 p-6">
                        <h2 className="text-lg font-bold text-gray-900">Centre-wise report</h2>
                        <p className="mt-1 text-sm text-gray-500">Detailed breakdown for {range.toLowerCase()}</p>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full min-w-[900px] table-fixed border-collapse text-left">
                            <thead>
                                <tr className="bg-gray-50 text-xs uppercase tracking-wide text-gray-400">
                                    <th className="w-[18%] px-6 py-3 font-medium">Centre</th>
                                    <th className="w-[14%] px-6 py-3 font-medium">Bookings</th>
                                    <th className="w-[16%] px-6 py-3 font-medium">Revenue</th>
                                    <th className="w-[18%] px-6 py-3 font-medium">Utilization</th>
                                    <th className="w-[14%] px-6 py-3 font-medium">Active users</th>
                                    <th className="w-[10%] px-6 py-3 font-medium">Machines</th>
                                    <th className="w-[10%] px-6 py-3 font-medium">Growth</th>
                                </tr>
                            </thead>
                            <tbody>
                                {CENTRE_DATA.map((c, idx) => (
                                    <tr
                                        key={c.centre}
                                        className={`text-sm ${idx !== CENTRE_DATA.length - 1 ? "border-b border-gray-100" : ""}`}
                                    >
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-indigo-500">
                                                    <Building2 className="h-4 w-4" />
                                                </span>
                                                <span className="font-semibold text-gray-900">APIC {c.centre}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-gray-600">{c.bookings}</td>
                                        <td className="px-6 py-4 font-medium text-gray-900">
                                            ₹{c.revenue.toLocaleString("en-IN")}
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-2 w-32">
                                                <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-gray-100">
                                                    <div
                                                        className="h-full rounded-full bg-indigo-500"
                                                        style={{ width: `${c.utilization}%` }}
                                                    />
                                                </div>
                                                <span className="w-9 shrink-0 text-xs text-gray-500">{c.utilization}%</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-gray-600">{c.activeUsers}</td>
                                        <td className="px-6 py-4 text-gray-600">{c.machines}</td>
                                        <td className="px-6 py-4">
                                            <span className={`flex items-center gap-1 text-xs font-semibold ${c.growth >= 0 ? "text-green-600" : "text-red-500"}`}>
                                                {c.growth >= 0 ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
                                                {Math.abs(c.growth)}%
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}